import {convertToAddressData, isSameAddress} from "../AddressStep/AddressStep";
import window from "../../../lib/WindowExtended";
import {DEFAULT_ERROR_MESSAGE, prepareTrackingProductData} from "../../Checkout";
import {Tracking} from "../../../../tracking/tracking";
import {ApiError, Checkout, ICartMessage, ICheckoutPutParams} from "../../../../typescript-library";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import {INTERNAL_ERROR_TYPE} from "../../../lib/payments/PaymentController";
import {captureException} from "@sentry/browser";
import {IShippingFormData} from "./ShippingForm";
import i18n from "i18next";

type SubmitReturnValue = {generalErrors?: string[]; phoneError?: string; canGoTo?: true};

export const submitShippingData = async (
  shippingData: IShippingFormData,
  checkout: Checkout,
): Promise<SubmitReturnValue | void> => {
  try {
    const {deliveryAddress, invoiceAddress} = checkout;

    const {shippingOption, phone, shippingDate, isPartDeliveryAllowed} = shippingData;
    const {isPhoneRequired, logisticsTypeId} = shippingOption;

    let shippingParams: ICheckoutPutParams = {
      favouredDateShipping: shippingDate,
      isPartDeliveryAllowed,
      logisticsTypeId,
    };

    if (deliveryAddress && phone && isPhoneRequired) {
      const deliveryConverted = convertToAddressData(deliveryAddress);
      if (isSameAddress(deliveryAddress, invoiceAddress)) {
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryConverted, phone},
            invoice: {...deliveryConverted, phone},
          },
        };
      } else if (invoiceAddress) {
        const invoiceConverted = convertToAddressData(invoiceAddress);
        shippingParams = {
          ...shippingParams,
          address: {
            delivery: {...deliveryConverted, phone},
            invoice: invoiceConverted,
          },
        };
      } else {
        return {
          generalErrors: [i18n.t("checkout.emptyInvoiceAddress")],
        };
      }
    }
    if (phone !== deliveryAddress?.phone) {
      window.isIntoSettingPhoneNoInShippingStep = true;
    }
    if (
      shippingParams.favouredDateShipping === checkout.favouredDateShipping &&
      shippingParams.isPartDeliveryAllowed === checkout.isPartDeliveryAllowed &&
      (shippingParams.logisticsTypeId ?? null) === checkout.logisticsTypeId &&
      phone === deliveryAddress?.phone
    ) {
      return {
        canGoTo: true,
      };
    }

    const shippingTier = shippingOption.name || "";
    const productData = prepareTrackingProductData(checkout.cartData);
    Tracking.sendAddShippingInfoEvent(shippingTier, productData);

    await checkout.put(shippingParams);
  } catch (err) {
    const tempError = ((err as ApiError).originalError || err) as PossibleNetworkError;
    const result = tempError.response?.data.result?.code || tempError.response?.data.result;
    const tempMessage: string = result || tempError.message;
    const tempMessages: ICartMessage[] | undefined = tempError.response?.data.messages;
    let errMessages: string[];
    if (tempMessages) {
      errMessages = tempMessages.map((msg) => msg.code);
    } else {
      errMessages = [tempMessage];
    }

    let sentryEventId: string | undefined;
    if (errMessages.some((msg) => !i18n.exists(msg, {ns: "errorcodes"}))) {
      tempError.name = INTERNAL_ERROR_TYPE.SHIPPING.OnCatch + tempError.name;
      sentryEventId = captureException(tempError, {extra: {place: "Shipping after checkout put", errMessages}});
    }

    const phoneError = errMessages.find((msg) => msg.toLowerCase().includes("phone"));

    return {
      phoneError,
      generalErrors: errMessages
        .filter((msg) => msg !== phoneError)
        .map((errMsg) => {
          const ns = ["translations", "errorcodes"];

          return i18n.t(errMsg, i18n.t(DEFAULT_ERROR_MESSAGE, {ns, sentryEventId}), {ns});
        }),
    };
  }
};
